// Global Instructions Rule Applied!
import React from 'react'
import { Button as AntButton } from 'antd'
import { portalColors } from '../../portal-configs/colors.js'

/**
 * Generic Button Component
 * Wraps Ant Design Button with consistent styling based on variant.
 *
 * Owner-approved scheme (2026-08-12): one primary colour. The former green
 * `save` variant now renders as the standard brand-blue primary — green is a
 * status colour (Booked/success), not an action colour. The old behaviour of
 * silently rewriting "Create" labels to "Save" is gone too: buttons say what
 * they do.
 *
 * @param {{ variant?: string, size?: string, children?: React.ReactNode, [antProp: string]: any }} props
 *   `children` is optional: icon-only buttons (Refresh, FormTable row actions, the delete ellipses) render
 *   with just `icon`. Everything else is forwarded to the Ant Design Button.
 */
const Button = ({ variant = 'default', size = 'default', children, ...props }) => {
  // Map size prop to Ant Design size
  const antSize = size === 'small' ? 'small' : size === 'default' ? 'middle' : size

  // Determine button props based on variant
  let buttonProps = { ...props }

  switch (variant) {
    case 'save':
    case 'primary':
      // The single confirmation-action colour: brand-blue primary (theme token)
      buttonProps = {
        ...buttonProps,
        type: 'primary'
      }
      break

    case 'delete':
      // Red danger color for delete buttons
      buttonProps = {
        ...buttonProps,
        danger: true
      }
      break

    case 'cancel':
      // Default styling for cancel buttons
      buttonProps = {
        ...buttonProps,
        type: 'default'
      }
      break

    case 'edit':
      // Orange warning color for edit buttons
      buttonProps = {
        ...buttonProps,
        type: 'default',
        style: {
          color: portalColors.warning,
          borderColor: portalColors.warning,
          ...buttonProps.style
        },
        className: `hover:!text-orange-700 hover:!border-orange-700 ${buttonProps.className || ''}`
      }
      break

    case 'default':
    default:
      // Default Ant Design styling
      buttonProps = {
        ...buttonProps,
        type: buttonProps.type || 'default'
      }
      break
  }

  return (
    <AntButton size={antSize} {...buttonProps}>
      {children}
    </AntButton>
  )
}

export default Button
